import { useState, useEffect } from "react"

const loadTrackingScripts = () => {
  if (typeof window === "undefined") return

  // Check if user has not consented
  if (window.localStorage.getItem("privacyConsent") === "false") {
    return
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || []

  // Push initial configuration
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  })

  // Load GTM
  if (process.env.GATSBY_GOOGLE_TAG_MANAGER) {
    const gtmScript = document.createElement("script")
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${process.env.GATSBY_GOOGLE_TAG_MANAGER}`
    gtmScript.async = true
    gtmScript.crossOrigin = "anonymous"
    document.head.appendChild(gtmScript)
  }

  // Load AdSense
  if (process.env.GATSBY_GOOGLE_ADSENSE) {
    const adsenseScript = document.createElement("script")
    adsenseScript.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${process.env.GATSBY_GOOGLE_ADSENSE}`
    adsenseScript.async = true
    adsenseScript.crossOrigin = "anonymous"
    document.head.appendChild(adsenseScript)
  }
}

const clearAllTracking = () => {
  // Remove GTM if it exists
  if (window.gtag) {
    window.gtag("consent", "update", {
      ad_storage: "denied",
      analytics_storage: "denied",
    })
  }

  // Remove GTM and AdSense scripts from DOM
  const trackingScripts = document.querySelectorAll(
    "script[src*='googletagmanager.com'], script[src*='googlesyndication.com']"
  )
  trackingScripts.forEach(script => script.remove())

  // Remove all iframes that might contain tracking
  const iframes = document.querySelectorAll(
    "iframe[src*='googletagmanager.com'], iframe[src*='googlesyndication.com']"
  )
  iframes.forEach(iframe => iframe.remove())

  // Clear dataLayer
  if (window.dataLayer) {
    window.dataLayer = []
  }

  // Remove all cookies
  const cookies = document.cookie.split(";")
  cookies.forEach(cookie => {
    const [name] = cookie.split("=")
    document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${
      window.location.hostname
    }`
    document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${
      window.location.hostname
    }`
  })

  // Clear all storage
  window.localStorage.clear()
  window.sessionStorage.clear()

  // Clear IndexedDB
  if (window.indexedDB) {
    const databases = window.indexedDB.databases()
    databases.then(dbs => {
      dbs.forEach(db => {
        window.indexedDB.deleteDatabase(db.name)
      })
    })
  }

  // Clear Cache Storage
  if (window.caches) {
    window.caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => {
        window.caches.delete(cacheName)
      })
    })
  }

  // Unregister all service workers
  if (navigator.serviceWorker) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister()
      })
    })
  }

  // Re-add privacyConsent flag
  window.localStorage.setItem("privacyConsent", "false")

  // Dispatch storage event to notify other tabs
  window.dispatchEvent(
    new StorageEvent("storage", {
      key: "privacyConsent",
      newValue: "false",
      oldValue: null,
      storageArea: window.localStorage,
    })
  )
}

export const usePrivacyConsent = () => {
  const [showBanner, setShowBanner] = useState(true)

  useEffect(() => {
    if (typeof window === "undefined") return

    // Check if user has already consented or opted out
    const consentStatus = window.localStorage.getItem("privacyConsent")
    setShowBanner(consentStatus === null)

    // If user has already consented, load tracking scripts immediately
    if (consentStatus === "true") {
      loadTrackingScripts()
    } else if (consentStatus === "false") {
      clearAllTracking()
    } else {
      // Otherwise, wait for a reasonable time (5 seconds) before loading tracking scripts
      const trackingTimer = setTimeout(() => {
        loadTrackingScripts()
      }, 5000)

      return () => clearTimeout(trackingTimer)
    }
  }, [])

  const handeOptIn = () => {
    if (typeof window === "undefined") return

    window.localStorage.setItem("privacyConsent", "true")
    setShowBanner(false)
    loadTrackingScripts()
  }

  const handleOptOut = () => {
    if (typeof window === "undefined") return

    setShowBanner(false)
    clearAllTracking()
  }

  return {
    showBanner,
    onOptIn: handeOptIn,
    onOptOut: handleOptOut,
  }
}
